import { DialogAlert } from "../DialogAlert";
import {
  DialogAlertI18nResource,
  DialogAlertProps,
  DialogAlertTemplateUnit,
  TemplateKeys
} from "../types";
import { getDialogTemplates } from "../templates";
import { create } from "zustand";
import i18n from "../locale/i18n";
import { useEffect } from "react";
import { i18nPrefix } from "../lib/constants";

type OpenDialogAlert = Omit<DialogAlertProps, "open" | "onOpenChange">;
type State = {
  open: boolean;
  setOpen: (data: boolean) => void;
  dialogProps: OpenDialogAlert;
  setDialogProps: (data: OpenDialogAlert) => void;
  openDialogAlert: (payload?: { template?: TemplateKeys; props?: OpenDialogAlert }) => void;
  closeDialogAlert: () => void;
};

const useDialogAlertStore = create<State>((set, get) => ({
  open: false,
  dialogProps: {},
  setOpen: (data) => set({ open: data }),
  setDialogProps: (data) => set({ dialogProps: data }),

  openDialogAlert: (payload) => {
    let templateVal: DialogAlertTemplateUnit | undefined;
    if (payload?.template) {
      templateVal = getDialogTemplates({ setOpen: get().setOpen })[payload.template];
      // add prefix
      templateVal.title = templateVal.title ? i18nPrefix + templateVal.title : undefined;
      templateVal.description =
        templateVal.description ? i18nPrefix + templateVal.description : undefined;
      templateVal.confirmText =
        templateVal.confirmText ? i18nPrefix + templateVal.confirmText : undefined;
      templateVal.cancelText = templateVal.cancelText ? i18nPrefix + templateVal.cancelText : undefined;
    }
    get().setDialogProps({ ...templateVal, ...payload?.props });
    get().setOpen(true);
  },
  closeDialogAlert: () => {
    set({ open: false });
  }
}));

interface DialogAlertProviderProps extends React.PropsWithChildren {
  /**
   * @description i18n resource for dialogAlert to use
   * @example 
   * ``` 
   * {
   *    en: {
   *      translation: {
   *        dialog_alert: {
   *         close: "Close",
   *         cancel: "Cancel",
   *         "success.saved": {
               title: "Successfully",
               description: "Data has been successfully saved"
              },
   *          ....
   *        }
   *       }
   *    }
   * }
   * ```
   */
  i18nResource?: DialogAlertI18nResource;
  /**
   * @description specify language to use
   */
  i18nLang?: string;
}
export const DialogAlertProvider = ({ children, i18nResource, i18nLang }: DialogAlertProviderProps) => {
  const open = useDialogAlertStore((state) => state.open);
  const setOpen = useDialogAlertStore((state) => state.setOpen);
  const dialogProps = useDialogAlertStore((state) => state.dialogProps);

  useEffect(() => {
    if (!i18nResource) {
      i18n.changeLanguage("sharedui");
      return;
    }
    try {
      const langs = Object.keys(i18nResource);
      for (const lang of langs) {
        i18n.addResourceBundle(lang, "translation", i18nResource[lang]["translation"], false, true);
      }
      i18n.changeLanguage(i18nLang);
    } catch {
      i18n.changeLanguage("sharedui");
      return;
    }
  }, [i18nLang, i18nResource]);

  return (
    <>
      {children}
      <DialogAlert open={open} onOpenChange={setOpen} {...dialogProps} />
    </>
  );
};

const openDialogAlert = useDialogAlertStore.getState().openDialogAlert;
const closeDialogAlert = useDialogAlertStore.getState().closeDialogAlert;
/**
 * @description open error dialog based on given error, if the error is javascript Error instance
 * it will use error.message as dialog description, if the error is AxiosError it will use error template
 * based on error.response.status
 *  @param error - Accept Error or AxiosError
 */
const openErrorDialogAlert = (error: unknown) => {
  if (error instanceof Error) {
    openDialogAlert({ template: "error.something_went_wrong", props: { description: error.message } });
  } else if ((error as any).isAxiosError) {
    let template: TemplateKeys = "error.something_went_wrong";
    switch ((error as any).response?.status) {
      case 400:
        template = "error.invalid_incomplete_data";
        break;
      case 401:
        template = "error.session_expired";
        break;
      case 403:
        template = "error.permission_denied";
        break;
      case 404:
        template = "error.data_not_found";
        break;
      case 409:
        template = "error.duplicate_data";
        break;
      case 500:
        template = "error.api_db_error";
        break;
    }
    openDialogAlert({ template });
  } else {
    openDialogAlert({ template: "error.something_went_wrong" });
  }
};

export const getDialogAlertControls = () => ({
  openDialogAlert,
  closeDialogAlert,
  openErrorDialogAlert
});
